import { call, put, takeEvery } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import {
    UPDATE_WORKOUT_SCHEDULE_DATE,
    UPDATE_WORKOUT_SCHEDULE_DATE_SUCCESS,
    DISPLAY_CALENDAR_MESSAGE,
    SEND_CALENDAR_ITEMS_TO_DEVICE_ERROR,
    FETCH_WORKOUT_BY_ID,
    FETCH_WORKOUT_BY_ID_FETCHING,
    FETCH_WORKOUT_BY_ID_FINISHED,
    SCHEDULE_WORKOUT_REQUEST,
    REMOVE_SCHEDULED_WORKOUT_REQUEST,
    FETCH_WORKOUT_SCHEDULE,
    FETCH_WORKOUTS_EXPORT,
    RESCHEDULE_WORKOUT,
    COPY_WORKOUT,
    DELETE_WORKOUT,
    UPLOAD_AUDIO_NOTE,
    DELETE_AUDIO_NOTE,
    FETCH_FBT_WORKOUT
} from '../actions/ActionTypes';
import {
    onRemoveScheduledWorkout,
    onScheduleWorkout,
    receiveWorkoutSchedule,
    receiveWorkoutsExport,
    requestWorkoutSchedule,
    requestWorkoutsExport
} from '../actions';
import { displayCalendarMessage } from '../actions/calendarActions';
import { handlingErrorCallback, handlingSuccessCallback } from './sagasUtils';
import { mobilityRoleKey } from 'models/preferences/PreferencesMapping'; // clean-up ticket CA-175696
import { mobilityPathSuffix } from 'WorkoutPage/utils/WorkoutUtil';
import axios from 'axios';

//workers
export function* updateWorkoutScheduleDate(action) {
    try {
        const { workoutScheduleId, calendarDate, workoutScheduleDTO } = action.payload;
        if (!workoutScheduleDTO && workoutScheduleId && calendarDate) {
            //we don't have the workoutScheduleDTO so we need to fetch it first before we can call update on it
            //therefore, in this scenario, workoutScheduleID and calendarDate is required
            const response = yield call(gcApi.get, `/workout-service/schedule/${workoutScheduleId}`);

            if (response) {
                response.data.calendarDate = calendarDate;
                yield call(gcApi.put, `/workout-service/schedule/${workoutScheduleId}`, response.data);
            }

            yield put({
                type: UPDATE_WORKOUT_SCHEDULE_DATE_SUCCESS,
                workoutSchedule: response.data,
                receivedAt: Date.now()
            });

            yield put({
                type: DISPLAY_CALENDAR_MESSAGE,
                show: true
            });
        } else {
            //we have the workoutScheduleDTO - just update it
            //note: if calendarDate is passed in, this will update the DTO, if not
            //assumption is that calendarDate was updated externally and is up-to-date in DTO
            if (calendarDate) {
                workoutScheduleDTO.calendarDate = calendarDate;
            }

            yield call(gcApi.put, `/workout-service/schedule/${workoutScheduleId}`, {
                workoutScheduleDTO: workoutScheduleDTO
            });

            yield put({
                type: UPDATE_WORKOUT_SCHEDULE_DATE_SUCCESS,
                workoutSchedule: workoutScheduleDTO,
                receivedAt: Date.now()
            });

            yield put({
                type: DISPLAY_CALENDAR_MESSAGE,
                show: true
            });
        }
    } catch (e) {
        yield put({
            type: SEND_CALENDAR_ITEMS_TO_DEVICE_ERROR
        });
    }
}

export function* fetchWorkoutById(action) {
    const { fullLocale, workoutId, savedWorkout, includeAudioNotes, viewerSocialProfile } = action.payload;
    let url = '';
    try {
        yield put({
            type: FETCH_WORKOUT_BY_ID_FETCHING
        });

        if (viewerSocialProfile) {
            const mobilityPreferenceResponse = yield call(axios.get, `/preference/${mobilityRoleKey}`, {
                baseURL: '/modern'
            });

            const mobilityRole = mobilityPreferenceResponse.data;

            const showMobilityWorkouts =
                viewerSocialProfile.userRoles &&
                mobilityRole.value &&
                viewerSocialProfile.userRoles.indexOf(mobilityRole.value) >= 0;

            url =
                savedWorkout === 'true'
                    ? `/workout-service/workout/${workoutId}`
                    : `/web-data/workouts/${fullLocale}/${workoutId}${
                          showMobilityWorkouts ? mobilityPathSuffix : ''
                      }.json`;
        } else {
            url =
                savedWorkout === 'true'
                    ? `/workout-service/workout/${workoutId}`
                    : `/web-data/workouts/${fullLocale}/${workoutId}.json`;
        }

        if (includeAudioNotes) {
            url += '?includeAudioNotes=true';
        }

        const response = yield call(gcApi.get, url, savedWorkout === 'true' ? {} : { baseURL: '/' });

        yield put({
            type: FETCH_WORKOUT_BY_ID_FINISHED,
            workoutById: response.data,
            receivedAt: Date.now()
        });
    } catch (e) {
        // fall back for the situation when the file with pathSuffix does not exist
        if (savedWorkout !== 'true' && url.includes(mobilityPathSuffix)) {
            yield call(fetchWorkoutById, {
                payload: {
                    fullLocale: fullLocale,
                    workoutId,
                    savedWorkout,
                    includeAudioNotes
                }
            });
        }

        // fall back for when a language is not found
        if (savedWorkout !== 'true' && fullLocale !== 'en-US') {
            yield call(fetchWorkoutById, {
                payload: {
                    fullLocale: 'en-US',
                    workoutId,
                    savedWorkout,
                    includeAudioNotes,
                    viewerSocialProfile
                }
            });
        }
    }
}

export function* scheduleWorkoutSaga({ workoutId, date, callback }) {
    try {
        yield put(onScheduleWorkout());
        const response = yield gcApi.post(`/workout-service/schedule/${workoutId}`, {
            date
        });
        yield put(displayCalendarMessage(true));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* removeScheduledWorkoutSaga({ workoutScheduleId, callback }) {
    try {
        yield put(onRemoveScheduledWorkout());
        const response = yield gcApi.delete(`/workout-service/schedule/${workoutScheduleId}`);
        yield put(displayCalendarMessage(true));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchWorkoutScheduleSaga({ workoutScheduleId, callback, includeAudioNotes }) {
    try {
        yield put(requestWorkoutSchedule());
        const response = yield gcApi.get(
            `/workout-service/schedule/${workoutScheduleId}${includeAudioNotes ? '?includeAudioNotes=true' : ''}`
        );
        let prevResponse = response;
        // Check for 3rd party workouts
        if (response.data.workout.workoutProvider !== null) {
            const workoutResponse = yield gcApi.get(
                `/workout-service/workout/${response.data.workout.workoutId}${
                    includeAudioNotes ? '?includeAudioNotes=true' : ''
                }`
            );
            prevResponse.data.workout = workoutResponse.data;
            yield put(receiveWorkoutSchedule(prevResponse.data, workoutScheduleId));
            yield call(handlingSuccessCallback, callback, prevResponse);
        } else {
            yield put(receiveWorkoutSchedule(response.data, workoutScheduleId));
            yield call(handlingSuccessCallback, callback, response);
        }
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchWorkoutsExportSaga({ date, callback }) {
    try {
        yield put(requestWorkoutsExport());
        const response = yield gcApi.get(`/workout-service/schedule/export/JSON?startDate=${date}`);
        yield put(receiveWorkoutsExport(response.data));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* rescheduleWorkoutSaga({ workout, workoutId, workoutScheduleId, date, callback }) {
    try {
        yield put(onScheduleWorkout());
        const response = yield gcApi.put(`/workout-service/schedule/${workoutScheduleId}`, {
            ...workout,
            calendarDate: date,
            workout: {
                workoutId
            }
        });
        yield put(displayCalendarMessage(true));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* copyWorkoutSaga({ workoutId, workoutName, callback }) {
    try {
        const response = yield call(gcApi.post, `/workout-service/workout/copy/${workoutId}`, workoutName, {
            headers: {
                'Content-Type': 'text/plain'
            }
        });
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* deleteWorkoutSaga({ workoutId, callback }) {
    try {
        const response = yield call(gcApi.delete, `/workout-service/workout/${workoutId}`);
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* uploadAudioNoteSaga({ audioBlob, callback }) {
    try {
        const form = new FormData();
        form.append('file', audioBlob, 'audio.mp3');
        const response = yield call(gcApi.post, '/workout-service/workout/audionote/upload', form);
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* deleteAudioNoteSaga({ audioUuid, callback }) {
    try {
        const response = yield call(gcApi.delete, `/workout-service/workout/audionote?audiouuid=${audioUuid}`);
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchfbtWorkoutSaga({ workoutUuid, callback }) {
    try {
        const response = yield call(gcApi.get, `/workout-service/fbt-adaptive/${workoutUuid}`);
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

//watcher
export const workoutSaga = [
    takeEvery(UPDATE_WORKOUT_SCHEDULE_DATE, updateWorkoutScheduleDate),
    takeEvery(FETCH_WORKOUT_BY_ID, fetchWorkoutById),
    takeEvery(SCHEDULE_WORKOUT_REQUEST, scheduleWorkoutSaga),
    takeEvery(REMOVE_SCHEDULED_WORKOUT_REQUEST, removeScheduledWorkoutSaga),
    takeEvery(FETCH_WORKOUT_SCHEDULE, fetchWorkoutScheduleSaga),
    takeEvery(FETCH_WORKOUTS_EXPORT, fetchWorkoutsExportSaga),
    takeEvery(RESCHEDULE_WORKOUT, rescheduleWorkoutSaga),
    takeEvery(COPY_WORKOUT, copyWorkoutSaga),
    takeEvery(DELETE_WORKOUT, deleteWorkoutSaga),
    takeEvery(UPLOAD_AUDIO_NOTE, uploadAudioNoteSaga),
    takeEvery(DELETE_AUDIO_NOTE, deleteAudioNoteSaga),
    takeEvery(FETCH_FBT_WORKOUT, fetchfbtWorkoutSaga)
];
